import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { supabase } from "../../data/supabase/client";

const OtpVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [type, setType] = useState<"recovery" | "invite" | null>(null);

  useEffect(() => {
    // Extract email and type from URL parameters
    const emailParam = searchParams.get("email");
    const typeParam = searchParams.get("type");

    if (!emailParam || !typeParam) {
      setError("Missing required parameters. Please use the link from your email.");
      return;
    }

    if (typeParam !== "recovery" && typeParam !== "invite") {
      setError("Invalid verification type.");
      return;
    }

    setEmail(emailParam);
    setType(typeParam as "recovery" | "invite");
  }, [searchParams]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!email || !type) {
      setError("Missing required parameters. Please use the link from your email.");
      return;
    }

    if (otp.length !== 6) {
      setError("Please enter a valid 6-digit code.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: type === "recovery" ? "recovery" : "invite"
      });

      if (error) {
        throw error;
      }

      if (data.session) {
        // Successfully verified OTP, redirect to password confirmation
        navigate("/reset-password-confirmation");
      } else {
        throw new Error("Verification failed. Please try again.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred during verification");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email || !type) {
      setError("Cannot resend code. Missing email information.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (type === "recovery") {
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        if (error) {
          throw error;
        }
      } else {
        // For invite type, we would need to trigger the invite resend
        // This might require a custom API call depending on how invites are handled
        setError("Please contact your administrator to resend the invite.");
        return;
      }

      setOtp("");
      alert("A new verification code has been sent to your email.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = (): string => {
    if (type === "recovery") {
      return "Verify Password Reset";
    }
    if (type === "invite") {
      return "Verify Account Invitation";
    }
    return "Verify Your Email";
  };

  const getDescription = (): string => {
    if (type === "recovery") {
      return "Enter the 6-digit code sent to your email to reset your password";
    }
    if (type === "invite") {
      return "Enter the 6-digit code sent to your email to activate your account";
    }
    return "Enter the 6-digit verification code";
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default"
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {getTitle()}
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
          {getDescription()}
        </Typography>

        {email && (
          <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
            Code sent to: <strong>{email}</strong>
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Verification Code"
              name="otp"
              type="text"
              value={otp}
              onChange={handleInputChange}
              required
              disabled={isLoading}
              placeholder="Enter 6-digit code"
              inputProps={{
                maxLength: 6,
                style: { textAlign: "center", fontSize: "1.5rem", letterSpacing: "0.25rem" }
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading || otp.length !== 6}
              sx={{ mt: 2 }}
            >
              {isLoading ? <CircularProgress size={24} /> : "Verify Code"}
            </Button>

            <Button
              fullWidth
              variant="text"
              onClick={handleResendCode}
              disabled={isLoading || type === "invite"}
              sx={{ mt: 1 }}
            >
              Resend Code
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default OtpVerification;
